import { computed, ComputedRef, ref } from 'vue';
import { DynamicCanvasSizes } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';

export type GuideType = 'wireGuide' | 'inoxGuide' | 'aluGuide';
export type GuideCorner = 'leftCorner' | 'rightCorner';
export type GuideWindowIndex = 1 | 2 | 3;

export interface GuideTypeOption {
    label: string;
    value: GuideType;
}

export interface GuideImageClickPayload {
    corner: GuideCorner;
    windowIndex: GuideWindowIndex;
    x: number;
    y: number;
}

export interface IndividualGuideTypes {
    1: {
        leftCorner: GuideType | null;
        rightCorner: GuideType | null;
    };
    2: {
        leftCorner: GuideType | null;
        rightCorner: GuideType | null;
    };
    3: {
        leftCorner: GuideType | null;
        rightCorner: GuideType | null;
    };
}

interface UseGuideImagesOptions {
    /** First guide selection (e.g. 'Enojno', 'Pregradno'). */
    guideType: () => string;
    /** Second guide selection. */
    secondGuideType: () => string;
    /** Whether the current window is the first in the combination. */
    isFirstWindow?: () => boolean;
    /** Whether the current window is the last in the combination. */
    isLastWindow?: () => boolean;
    /** Whether the configurator is on the final step (guides visible). */
    isLastStep?: () => boolean;
    /** Position of the current window (1-based) or null. */
    windowPosition?: () => number | null;
    /** Rendered frame width (after scaling) — anchors right/middle images. */
    frameWidth?: () => number;
    /** Reactive guide types for the current window, supplied by the parent. */
    windowGuides?: () => IndividualGuideTypes[1];
    /** Per-product dynamic sizing/asset configuration. */
    canvasTemplateConfiguration?: () => DynamicCanvasSizes;
}

/**
 * Encapsulates the rules for which guide image (left/right/middle) is shown
 * on a given window in the combinations canvas, plus the Konva image configs.
 *
 * Visibility rules and image positioning are configurator-wide and shared
 * across canvas layouts, so they live here instead of being duplicated.
 */
export function useGuideImages(options: UseGuideImagesOptions) {
    const {
        guideType,
        secondGuideType,
        isFirstWindow = () => false,
        isLastWindow = () => false,
        isLastStep = () => false,
        windowPosition = () => null,
        frameWidth = () => 0,
        windowGuides = () => ({ leftCorner: null, rightCorner: null }),
        canvasTemplateConfiguration = (): DynamicCanvasSizes => ({
            guides: { left: '', right: '', center: '' },
            scale: 1,
            guideLeftOffset: 0,
            rightOffset: 0,
            rightOffsetX: 0,
            middleGuideOffsetX: 0,
            guideYOffset: 0,
            descriptionYOffset: 0,
            windowGroupYOffset: 0,
            extraMarginForCanvasStage: 0,
            guideRightOffsetY: 0,
        }),
    } = options;
    // Here we track the individual guide types for each window
    const individualGuideTypes = ref(createEmptyIndividualGuideTypes());
    const guideTypeOptions: GuideTypeOption[] = [
        { label: 'Wire guides', value: 'wireGuide' },
        { label: 'Inox guides', value: 'inoxGuide' },
        { label: 'Alu guides', value: 'aluGuide' },
    ];

    function setIndividualGuideType(windowIndex: GuideWindowIndex, corner: GuideCorner, type: GuideType | null) {
        console.log(individualGuideTypes.value, windowIndex, corner, type);
        individualGuideTypes.value[windowIndex][corner] = type;
    }

    // Predicates for guide types and combinations

    const isFirstGuideSingle = computed(() => guideType() === 'Enojno');
    const isSecondGuideSingle = computed(() => secondGuideType() === 'Enojno');
    const areBothGuidesSingle = computed(() => isFirstGuideSingle.value && isSecondGuideSingle.value);

    const isFirstGuidePartition = computed(() => guideType() === 'Pregradno');
    const isSecondGuidePartition = computed(() => secondGuideType() === 'Pregradno');
    const areBothGuidesPartition = computed(() => isFirstGuidePartition.value && isSecondGuidePartition.value);

    const isAnyGuideSelected = computed(() => !!guideType() || !!secondGuideType());

    const shouldShowPartitionGuide = computed(() => {
        const position = windowPosition();

        if (!position) return false;

        return (isFirstGuidePartition.value && position === 1) || (isSecondGuidePartition.value && position === 2);
    });

    const showLeftGuideImage: ComputedRef<boolean> = computed(() => {
        if (!isLastStep()) return false;
        if (isFirstWindow()) return true;
        if (!isAnyGuideSelected.value) return false;
        if (areBothGuidesPartition.value) return false;
        if (areBothGuidesSingle.value) return true;
        if (isFirstGuideSingle.value && windowPosition() === 2) return true;
        if (isSecondGuideSingle.value && windowPosition() === 3) return true;
        return false;
    });

    const showRightGuideImage: ComputedRef<boolean> = computed(() => {
        if (!isLastStep()) return false;
        if (isLastWindow()) return true;
        if (!isAnyGuideSelected.value) return false;
        if (areBothGuidesPartition.value) return false;
        if (areBothGuidesSingle.value) return true;
        if (isFirstGuideSingle.value && windowPosition() === 1) return true;
        if (isSecondGuideSingle.value && windowPosition() === 2) return true;
        return false;
    });

    const showMiddleGuideImage = computed(() => shouldShowPartitionGuide.value && isAnyGuideSelected.value);

    function getGuideScaleFactor(): number {
        return canvasTemplateConfiguration().scale ?? 1.5;
    }

    const leftGuideImageConfig = computed(() => {
        const position = windowPosition();
        const sizes = canvasTemplateConfiguration();
        const scaleFactor = getGuideScaleFactor();
        const offset = sizes.guideLeftOffset ?? -30;
        const guideYOffset = sizes.guideYOffset ?? -45;

        const imageObj = new Image();
        const imageType = windowGuides().leftCorner;
        console.log(getGuideImageSource(sizes, 'left', imageType), position, individualGuideTypes.value);
        imageObj.src = getGuideImageSource(sizes, 'left', imageType);

        return {
            x: 0 - offset,
            y: guideYOffset,
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    });

    const rightGuideImageConfig = computed(() => {
        const sizes = canvasTemplateConfiguration();
        const scaleFactor = getGuideScaleFactor();
        const offset = sizes.rightOffset ?? -30;
        const guideYOffset = sizes.guideRightOffsetY ?? sizes.guideYOffset ?? -45;
        const rightOffsetX = sizes.rightOffsetX ?? 80;

        const imageObj = new Image();
        const imageType = windowGuides().rightCorner;

        imageObj.src = getGuideImageSource(sizes, 'right', imageType);

        return {
            x: frameWidth() + offset,
            y: guideYOffset,
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            offsetX: rightOffsetX,
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    });

    const middleGuideImageConfig = computed(() => {
        const sizes = canvasTemplateConfiguration();
        const scaleFactor = getGuideScaleFactor();
        const guideYOffset = sizes.guideYOffset ?? -45;
        const middleGuideOffsetX = sizes.middleGuideOffsetX ?? frameWidth() / 2;

        const imageObj = new Image();
        imageObj.src = sizes.guides.center;

        return {
            x: frameWidth(),
            y: guideYOffset,
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            offsetX: middleGuideOffsetX,
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    });

    return {
        // State
        individualGuideTypes,
        guideTypeOptions,
        setIndividualGuideType,

        // Guide-type predicates
        isFirstGuideSingle,
        isSecondGuideSingle,
        areBothGuidesSingle,
        isFirstGuidePartition,
        isSecondGuidePartition,
        areBothGuidesPartition,
        isAnyGuideSelected,
        shouldShowPartitionGuide,

        // Visibility flags
        showLeftGuideImage,
        showRightGuideImage,
        showMiddleGuideImage,

        // Konva image configs
        leftGuideImageConfig,
        rightGuideImageConfig,
        middleGuideImageConfig,
    };
}

function getGuideImageSource(
    sizes: DynamicCanvasSizes,
    corner: 'left' | 'right',
    type: GuideType | null | undefined,
): string {
    if (type === 'wireGuide') {
        return sizes.guides.wireGuide ?? sizes.guides[corner];
    }

    if (type === 'inoxGuide') {
        return sizes.guides.inoxGuide ?? sizes.guides[corner];
    }

    return sizes.guides[corner];
}

function createEmptyIndividualGuideTypes(): IndividualGuideTypes {
    return {
        1: {
            leftCorner: 'aluGuide',
            rightCorner: 'aluGuide',
        },
        2: {
            leftCorner: 'aluGuide',
            rightCorner: 'aluGuide',
        },
        3: {
            leftCorner: 'aluGuide',
            rightCorner: 'aluGuide',
        },
    };
}
